import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { createCategory, updateCategory, getAllCategories } from '../redux/slice/category.slice';
import { ArrowLeft, CheckCircle, Tag, Info, FileText, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const HelperText = ({ text }) => (
    <p className="mt-1.5 text-xs text-slate-500 flex items-start gap-1.5 pl-1">
        <Info className="w-3.5 h-3.5 text-secondary flex-shrink-0 mt-0.5" />
        <span className="italic opacity-80">{text}</span>
    </p>
);

const SectionHeader = ({ title, icon: Icon }) => (
    <div className="flex items-center gap-3 mb-6 border-b border-gray-100 pb-4">
        <div className="p-2.5 bg-slate-50 rounded-lg text-secondary">
            <Icon className="w-5 h-5" />
        </div>
        <h2 className="text-xl font-serif font-semibold text-primary">{title}</h2>
    </div>
);

const CategoryForm = () => {
    const { id } = useParams();
    const isEdit = !!id;
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { categories, loading, error } = useSelector((state) => state.category);

    useEffect(() => {
        if (categories.length === 0) {
            dispatch(getAllCategories());
        }
    }, [dispatch, categories.length]);

    const validationSchema = Yup.object({
        name: Yup.string().required('Name is required'),
        description: Yup.string()
    });

    const formik = useFormik({
        initialValues: {
            name: '',
            description: '',
        },
        validationSchema,
        onSubmit: async (values) => {
            if (isEdit) {
                await dispatch(updateCategory({ id, data: values }));
            } else {
                await dispatch(createCategory(values));
            }
            navigate('/categories');
        },
    });

    // Sync Formik values when data is loaded (for edit mode)
    useEffect(() => {
        if (isEdit && categories.length > 0) {
            const category = categories.find(c => c._id === id);
            if (category) {
                formik.setValues({
                    name: category.name || '',
                    description: category.description || '',
                });
            }
        }
    }, [categories, id, isEdit]);

    return (
        <div className="min-h-[calc(100vh-4rem)] bg-surface font-sans text-primary ">
            {/* Top Navigation Bar with Blur Effect */}
            <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center h-auto lg:h-20 py-4 lg:py-0 gap-4 lg:gap-0">
                        <div className="flex items-center gap-4">
                            <Link
                                to="/categories"
                                className="p-2 ml-[-8px] text-slate-400 hover:text-secondary hover:bg-secondary/5 rounded-full transition-all"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </Link>
                            <div>
                                <h1 className="text-lg md:text-2xl font-serif font-bold text-primary tracking-tight">
                                    {isEdit ? 'Edit Category' : 'Add Category'}
                                </h1>
                                <p className="hidden md:block text-xs text-slate-500 font-medium tracking-wide uppercase">
                                    {isEdit ? 'Update Details' : 'New Classification'}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Link
                                to="/categories"
                                className="px-3 py-2 md:px-5 md:py-2.5 border border-gray-200 text-gray-600 font-medium text-xs md:text-sm rounded-xl hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </Link>
                            <button
                                type="button"
                                onClick={formik.handleSubmit}
                                disabled={loading}
                                className="px-4 py-2 md:px-6 md:py-2.5 bg-primary text-white font-medium text-xs md:text-sm rounded-xl hover:bg-tertiary transition-all shadow-lg shadow-blue-900/10 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        <span className="hidden sm:inline">Saving...</span>
                                        <span className="sm:hidden">Saving</span>
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="w-4 h-4" />
                                        <span className="hidden sm:inline">
                                            {isEdit ? 'Save Changes' : 'Create Category'}
                                        </span>
                                        <span className="sm:hidden">
                                            {isEdit ? 'Save' : 'Create'}
                                        </span>
                                    </>

                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

                {error && (
                    <div className="mb-8 p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 flex items-center gap-3 shadow-sm animate-fade-in">
                        <div className="p-2 bg-white rounded-full shadow-sm text-red-500">
                            <X className="w-4 h-4" />
                        </div>
                        <span className="font-medium">{error}</span>
                    </div>
                )}

                <form onSubmit={formik.handleSubmit} className="space-y-8">
                    {/* SECTION 1: Category Details */}
                    <div className="bg-white p-4 sm:p-8 rounded-2xl shadow-sm border border-slate-100">
                        <SectionHeader title="Category Details" icon={Tag} />
                        <div className="grid grid-cols-1 gap-6 max-w-2xl">
                            {/* Name */}
                            <div>
                                <label className="block text-sm font-semibold text-primary mb-2">
                                    Category Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    className="w-full px-5 py-3 border border-gray-200 rounded-xl focus:ring-4 focus:ring-secondary/10 focus:border-secondary transition-all outline-none bg-slate-50/50 text-lg font-medium placeholder:font-sans placeholder:font-normal"
                                    placeholder="e.g. Residential"
                                />
                                {formik.touched.name && formik.errors.name ? (
                                    <p className="mt-1 text-sm text-red-500 font-medium">{formik.errors.name}</p>
                                ) : (
                                    <HelperText text="A unique name for this project category" />
                                )}
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-semibold text-primary mb-2">
                                    Description <span className="text-slate-400 font-normal">(Optional)</span>
                                </label>
                                <textarea
                                    name="description"
                                    value={formik.values.description}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    rows={5}
                                    className="w-full px-5 py-4 border border-gray-200 rounded-xl focus:ring-4 focus:ring-secondary/10 focus:border-secondary transition-all outline-none bg-slate-50/50 leading-relaxed placeholder:text-gray-400"
                                    placeholder="Briefly describe what this category entails..."
                                />
                                {formik.touched.description && formik.errors.description ? (
                                    <p className="mt-1 text-sm text-red-500 font-medium">{formik.errors.description}</p>
                                ) : (
                                    <HelperText text="Additional details for internal reference" />
                                )}
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CategoryForm;
